import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import AddIcon from '@mui/icons-material/Add';
import ReplayIcon from '@mui/icons-material/Replay';

import Chip from '@shared/atoms/Chip';
import DragAndDrop from '@shared/molecules/DragAndDrop';
import Drawer from '@shared/atoms/Drawer';
import Button from '@shared/atoms/Button';
import T from '@shared/atoms/Typography';
import Tooltip from '@shared/atoms/Tooltip';
import ItemBox from '@shared/molecules/ItemBox';
import Dialog from '@shared/molecules/Dialog';

import { logger, newSelectTranslation } from '@shared/utils';
import theme from '@shared/themes/default';

import {
  contentType,
  courseStatus,
  DOMAIN_LOCALES,
  EPISODE_STATUS,
  EPISODE_TYPE,
  PROCESSING_STATUS,
  PROCESSING_STATUS_PRIORITY,
} from '@shared/const';

import { useAppGlobals } from '../../../../contexts/AppContext';
import { TABS, useCourse } from '../../../../contexts/CourseContext';
import {
  hasAllEpisodesWithoutSubtitles,
  validateSubtitleLanguages,
} from '../../utils/episodeValidation';

import Box from './components/Box';
import DrawerBatch from './components/DrawerBatch';
import DrawerLesson from './components/DrawerLesson';
import { DRAWER_ACTIONS } from './const';
import LessonCustomHeader from './components/LessonCustomHeader';
import ValidationList from '../../components/ValidationList';

const SectionStyled = styled.section`
  background: ${theme.palette.darkWrapper};
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.s};
  row-gap: ${theme.spacing.m1};
`;

const HeaderStyled = styled.header`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: ${theme.spacing.s};
`;

const ButtonGroupContainer = styled.div`
  align-items: center;
  column-gap: ${theme.spacing.m1};
  display: flex;
`;

const ReplaceLessonsList = styled.div`
  margin-top: ${theme.spacing.m1};
  max-height: 200px;
  overflow-y: auto;
`;

const LESSON_CONTENT = {
  SUBTITLE: 'subtitle',
  VIDEO: 'video',
};

const Lessons = () => {
  const { domain, locale } = useAppGlobals();
  const { t } = useTranslation();
  const languages = DOMAIN_LOCALES[domain];

  const {
    course: originalCourse,
    currentTabValidation,
    enabledLanguages,
    getLanguages,
    hasChanged,
    fetchLessonsProcessingStatus,
    locked,
    publishDraftEpisodes,
    saveLessons,
    setCurrentTabValidation,
    setHandleSave,
    setHasChanged,
    setHasError,
    setUploadingFiles,
    submitting,
    submittingRefresh,
    uploadingFiles,
    validateCourse,
  } = useCourse();

  const [course, setCourse] = useState(originalCourse || {});
  const [episodes, setEpisodes] = useState([]);
  const [drawer, setDrawer] = useState('');
  const [isOpenDrawer, setIsOpenDrawer] = useState(false);
  const [errorsDrawerLesson, setErrorsDrawerLesson] = useState([]);
  const [selectedEpisode, setSelectedEpisode] = useState();
  const [originalSelectedEpisode, setOriginalSelectedEpisode] = useState();
  const [batchEpisodes, setBatchEpisodes] = useState({});
  const [errorEpisodes, setErrorEpisodes] = useState({});
  const [changedDrawerContent, setChangedDrawerContent] = useState(false);
  const [errorDrawer, setErrorDrawer] = useState(null);
  const [subtitleError, setSubtitleError] = useState(null);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [batchOverwrite, setBatchOverwrite] = useState([]);
  const [confirmationDialogData, setConfirmationDialogData] = useState();
  const [canSave, setCanSave] = useState(false);
  const [publishDraftDialogOpen, setPublishDraftDialogOpen] = useState(false);

  const isPublished = course?.status === courseStatus.PUBLISHED;
  const isLocked = submitting || (isPublished && locked);
  const hasTrailer =
    course.trailer?.videoFiles?.length > 0 ||
    course.trailer?.file?.processingStatus;
  const initialLanguages = getLanguages(course);
  const presentLanguages = Object.keys(initialLanguages).reduce(
    (acc, lang) => (initialLanguages[lang] ? [...acc, lang] : acc),
    []
  );

  const draftEpisodes = (course.episodes || []).filter(
    (ep) => ep.status === EPISODE_STATUS.DRAFT
  );
  const hasDraftEpisodes = draftEpisodes.length > 0;

  const activeVideoEpisodes = (course.episodes || []).filter(
    (ep) =>
      ep.status === EPISODE_STATUS.ACTIVE && ep.contentType !== contentType.GAME
  );
  const activeEpisodesHaveSubtitles =
    activeVideoEpisodes.length > 0 &&
    activeVideoEpisodes.every((ep) => (ep.content?.subtitles?.length || 0) > 0);

  const isDraftEpisodeReady = (ep) => {
    if (ep.contentType === contentType.GAME) return true;
    const hasVideoFiles = (ep.content?.videoFiles || []).length > 0;
    const fileStatus = ep.content?.file?.processingStatus;
    if (!hasVideoFiles && !fileStatus) return false;
    const videoReady =
      hasVideoFiles || fileStatus === PROCESSING_STATUS.PROCESSED;
    const subtitlesReady = (ep.content?.subtitles || []).every(
      (sub) => sub.file?.processingStatus === PROCESSING_STATUS.PROCESSED
    );
    const attachmentsReady = (ep.attachments || []).every(
      (att) => att.file?.processingStatus === PROCESSING_STATUS.PROCESSED
    );
    if (activeEpisodesHaveSubtitles && !(ep.content?.subtitles?.length > 0)) {
      return false;
    }
    return videoReady && subtitlesReady && attachmentsReady;
  };

  const allDraftEpisodesProcessed =
    hasDraftEpisodes && draftEpisodes.every(isDraftEpisodeReady);
  const canPublishDrafts = locked && !hasChanged && allDraftEpisodesProcessed;

  const sortEpisodes = () =>
    [...course.episodes].sort((a, b) => a.ordinal - b.ordinal);

  const hasEpisodeError = (episode) => {
    enabledLanguages.reduce((acc, language) => {
      const hasName =
        episode.name && newSelectTranslation(course.name, language, '');

      const isActive = hasName;

      return acc || isActive;
    }, false);
  };

  const handleSave = (newCourse) => {
    try {
      saveLessons(newCourse);
      setHasChanged(false);
    } catch (error) {
      logger.error(error);
      setHasError(true);
    }
  };

  /**
   * Return true if there are episodes without subtitles or no episode has subtitle
   *
   * @param modifiedEpisodes
   * @param originalEpisodes
   * @return {boolean}
   */
  const handlePreSave = () => {
    const newCourse = { id: course.id };
    let changedVideoFiles = 0;

    if (
      course.trailer?.file?.processingStatus === PROCESSING_STATUS.TO_SCHEDULE
    ) {
      newCourse.trailerVideo = course.trailer.file.file;
      if (originalCourse.trailer?.file?.processingStatus)
        changedVideoFiles += 1;
    } else if (
      (course.trailer?.videoFiles || []).length === 0 &&
      !course.trailer?.file?.processingStatus &&
      originalCourse.trailer?.file?.processingStatus
    ) {
      newCourse.trailerVideo = null;
    }

    newCourse.trailerSubtitles = (course.trailer?.subtitles || []).map(
      ({ file: { file, processingStatus }, language }) => {
        const result = { language };

        if (processingStatus === PROCESSING_STATUS.TO_SCHEDULE) {
          result.file = file;
        }

        return result;
      }
    );

    newCourse.episodes = (course.episodes || []).reduce(
      (acc, originalEpisode) => {
        const episode = course.episodes.find(
          (episode) => episode.id === originalEpisode.id
        );

        if (episode.hasChanged || episode.isNew) {
          const newEpisode = { name: episode.name, ordinal: episode.ordinal };
          if (!episode.isNew) {
            newEpisode.id = episode.id;
          }

          if (
            episode.content?.file?.processingStatus ===
            PROCESSING_STATUS.TO_SCHEDULE
          ) {
            newEpisode.video = episode.content.file.file;
            if (
              originalEpisode?.content?.file?.processingStatus &&
              originalEpisode?.status === EPISODE_STATUS.ACTIVE
            )
              changedVideoFiles += 1;
          } else if (
            (episode.content?.videoFiles || []).length === 0 &&
            !episode.content?.file?.processingStatus
          ) {
            newEpisode.video = null;
          }

          newEpisode.subtitles = (episode.content?.subtitles || []).map(
            ({ file: { file, processingStatus }, language }) => {
              const result = { language };

              if (processingStatus === PROCESSING_STATUS.TO_SCHEDULE) {
                result.file = file;
              }

              return result;
            }
          );

          if (episode.contentType === contentType.GAME) {
            newEpisode.game = {
              duration: episode.content.duration,
              externalGameId: episode.content.externalGameId,
              type: episode.content.type,
            };
            newEpisode.video = null;
            newEpisode.subtitles = [];
          }

          const originalEpisodeFromOriginalCourse =
            originalCourse.episodes.find((ep) => ep.id === episode.id);
          const originalAttachments =
            originalEpisodeFromOriginalCourse?.attachments || [];
          const currentAttachments = episode.attachments || [];

          const attachmentsToCreate = [];
          const attachmentsToUpdate = [];
          const attachmentsToDelete = [];

          currentAttachments.forEach(
            ({ id, file: { file, processingStatus }, name }) => {
              const attachment = { name };

              if (processingStatus === PROCESSING_STATUS.TO_SCHEDULE) {
                attachment.file = file;
              }

              if (id) {
                attachment.id = id;
                attachmentsToUpdate.push(attachment);
              } else {
                attachmentsToCreate.push(attachment);
              }
            }
          );

          originalAttachments.forEach((originalAttachment) => {
            if (originalAttachment.id) {
              const stillExists = currentAttachments.some(
                (current) => current.id === originalAttachment.id
              );
              if (!stillExists) {
                attachmentsToDelete.push(originalAttachment.id);
              }
            }
          });

          if (
            attachmentsToCreate.length > 0 ||
            attachmentsToUpdate.length > 0 ||
            attachmentsToDelete.length > 0
          ) {
            newEpisode.attachments = {};
            if (attachmentsToCreate.length > 0) {
              newEpisode.attachments.create = attachmentsToCreate;
            }
            if (attachmentsToUpdate.length > 0) {
              newEpisode.attachments.update = attachmentsToUpdate;
            }
            if (attachmentsToDelete.length > 0) {
              newEpisode.attachments.delete = attachmentsToDelete;
            }
          }

          return [...acc, newEpisode];
        }
        return [...acc, { id: episode.id }];
      },
      []
    );

    if (isPublished && changedVideoFiles > 0) {
      setConfirmationDialogData({
        newCourse,
        changedVideoFiles,
      });
      setConfirmationDialogOpen(true);
      return;
    }
    handleSave(newCourse);
  };

  const handlerHasChanged = () => {
    const originalCourseParsed = JSON.stringify(originalCourse);
    const courseParsed = JSON.stringify(course);

    if (!hasChanged && originalCourseParsed !== courseParsed) {
      setHasChanged(true);
    }
    if (hasChanged && originalCourseParsed === courseParsed) {
      setHasChanged(false);
    }
  };
  useEffect(() => {
    setHandleSave(() => handlePreSave);
  }, []);

  useEffect(() => {
    setEpisodes(sortEpisodes());
    setErrorEpisodes(
      episodes.reduce(
        (acc, episode) => ({ ...acc, [episode.id]: hasEpisodeError(episode) }),
        {}
      )
    );

    if (course?.status === courseStatus.PUBLISHED) {
      const validationStatus = validateCourse(
        course,
        languages,
        hasAllEpisodesWithoutSubtitles(originalCourse?.episodes)
      );
      if (validationStatus[TABS.LESSONS]?.episodes?.length) {
        const episodesValidation = validationStatus[
          TABS.LESSONS
        ].episodes.reduce((acc, { subtitle, index }) => {
          const episode = course.episodes.find((ep) => ep.ordinal === index);
          if (
            subtitle &&
            (!episode?.content?.subtitles ||
              episode?.content?.subtitles.length === 0)
            // TODO: better check on multilanguage subtitles
          ) {
            // Leave out attachment checking for published courses
            acc.push({ subtitle, index });
          }
          return acc;
        }, []);

        if (episodesValidation.length > 0) {
          validationStatus[TABS.LESSONS] = {
            episodes: episodesValidation,
          };
        } else {
          delete validationStatus[TABS.LESSONS];
        }
      }

      setCurrentTabValidation(validationStatus);
    }

    handlerHasChanged();

    setHandleSave(() => handlePreSave);
  }, [course]);

  useEffect(() => {
    setCourse(originalCourse || {});
  }, [originalCourse]);

  useEffect(() => {
    if (
      selectedEpisode &&
      selectedEpisode.id !== 'trailer' &&
      !selectedEpisode.items
    ) {
      const requiredLanguages = isPublished
        ? presentLanguages
        : presentLanguages.slice(0, 1);
      setErrorsDrawerLesson(
        requiredLanguages.filter(
          (lang) => !newSelectTranslation(selectedEpisode.name, lang)
        )
      );
    } else {
      setErrorsDrawerLesson([]);
    }
  }, [selectedEpisode]);

  useEffect(() => {
    const hasEpisodeError = Object.keys(errorEpisodes).reduce(
      (acc, id) => acc || errorEpisodes[id],
      false
    );
    if (hasChanged && !isLocked && !hasEpisodeError && !uploadingFiles) {
      if (isPublished) {
        const activeEpisodes = course.episodes.filter(
          (episode) => episode.status === EPISODE_STATUS.ACTIVE
        );
        const currentValidation = validateSubtitleLanguages(activeEpisodes);
        const originalValidation = validateSubtitleLanguages(
          originalCourse.episodes
        );

        let subtitleValidationPassed = false;

        if (!originalValidation.allNone) {
          subtitleValidationPassed =
            !currentValidation.allNone && !currentValidation.isMixed;
        } else {
          subtitleValidationPassed =
            currentValidation.allNone || !currentValidation.isMixed;
        }

        const videoProcessingValid = activeEpisodes.reduce((acc, episode) => {
          const status = episode.content?.file?.processingStatus;
          return (
            acc &&
            (episode.contentType === EPISODE_TYPE.GAME ||
              status === PROCESSING_STATUS.TO_SCHEDULE ||
              status === PROCESSING_STATUS.PROCESSING ||
              status === PROCESSING_STATUS.PROCESSED ||
              (episode.content?.videoFiles || []).length > 0)
          );
        }, true);

        const canSave = subtitleValidationPassed && videoProcessingValid;
        setCanSave(canSave);
      } else {
        setCanSave(true);
      }
    } else {
      setCanSave(false);
    }
  }, [hasChanged, isLocked, errorEpisodes, course, uploadingFiles]);

  useEffect(() => {
    if (hasChanged && !canSave) {
      const validationErrors = {
        [TABS.LESSONS]: {
          episodes: course.episodes
            .filter((episode) => episode.contentType !== contentType.GAME)
            .map((episode) => {
              const episodeErrors = {};

              const hasVideo =
                episode.content?.videoFiles?.length > 0 ||
                episode.content?.file?.processingStatus;
              if (!hasVideo) {
                episodeErrors.video = true;
              }

              if (isPublished) {
                const originalEpisode = originalCourse.episodes.find(
                  (e) => e.id === episode.id
                );
                const hasSubtitlesOriginal =
                  originalEpisode?.content?.subtitles?.length > 0;
                const hasSubtitlesCurrent =
                  episode.content?.subtitles?.length > 0;

                if (hasSubtitlesOriginal && !hasSubtitlesCurrent) {
                  episodeErrors.subtitle = true;
                }
              }

              return {
                index: episode.ordinal,
                ...episodeErrors,
              };
            })
            .filter((episodeError) => Object.keys(episodeError).length > 1),
        },
      };

      if (validationErrors[TABS.LESSONS].episodes.length > 0) {
        setCurrentTabValidation(validationErrors);
      } else {
        setCurrentTabValidation({});
      }
    } else if (hasChanged && canSave) {
      setCurrentTabValidation({});
    }
  }, [
    canSave,
    hasChanged,
    course.episodes,
    isPublished,
    originalCourse.episodes,
  ]);

  const stringify = (object) => {
    if (!object) return '';
    return JSON.stringify(
      Object.keys(object)
        .filter((key) => key !== 'hasChanged')
        .sort()
        .reduce(
          (acc, key) => ({
            ...acc,
            [key]: object[key],
          }),
          {}
        )
    );
  };

  const handleOpenDrawer = (type) => {
    setDrawer(type);
    setIsOpenDrawer(true);
    setChangedDrawerContent(false);
  };

  const handleCloseDrawer = () => {
    setIsOpenDrawer(false);
    setDrawer('');
    setSelectedEpisode();
    setOriginalSelectedEpisode();
    setErrorDrawer(null);
    setSubtitleError(null);
    setUploadingFiles(false);
    handlerHasChanged();
  };

  const handleDeleteTrailer = () => {
    setCourse({
      ...course,
      trailer: null,
    });
  };

  const handleDeleteEpisode = (id) => {
    const filteredEpisodes = episodes.filter((episode) => episode.id !== id);
    setCourse({
      ...course,
      episodes: filteredEpisodes.map((episode, i) => {
        const ordinal = i + 1;
        return {
          ...episode,
          hasChanged: episode.hasChanged || episode.ordinal !== ordinal,
          ordinal,
        };
      }),
    });
  };

  const handleDragEndEpisode = (newEpisodes) => {
    setCourse({
      ...course,
      episodes: newEpisodes.map((episode, i) => {
        const ordinal = i + 1;
        return {
          ...episode,
          hasChanged: episode.hasChanged || ordinal !== episode.ordinal,
          ordinal,
        };
      }),
    });
  };

  const handleChangeBatchDrawer = (newItems) => {
    setBatchEpisodes(newItems);
    setChangedDrawerContent(newItems && Object.keys(newItems)?.length > 0);
  };

  const handleChangeDrawer = (newItem, saveEnabled = true) => {
    const hasChangedDrawer =
      stringify(newItem) !== stringify(originalSelectedEpisode);

    const originalEpisode = originalCourse.episodes.find(
      (e) => e.id === newItem.id
    );
    const hasChanged = stringify(newItem) !== stringify(originalEpisode);

    const newSelectedEpisode = newItem;
    if (saveEnabled && hasChanged) {
      newSelectedEpisode.hasChanged = true;
    }

    setSelectedEpisode(newSelectedEpisode);
    setChangedDrawerContent(saveEnabled && hasChangedDrawer);
  };

  const handleSaveLessonDrawer = () => {
    let newCourse;
    if (selectedEpisode.id === 'trailer') {
      newCourse = {
        ...course,
        trailer: selectedEpisode.content,
      };
    } else if (selectedEpisode.id) {
      const idx = course.episodes.findIndex((e) => e.id === selectedEpisode.id);
      if (idx >= 0) {
        const cpyEpisodes = [...course.episodes];
        cpyEpisodes[idx] = selectedEpisode;
        newCourse = { ...course, episodes: cpyEpisodes };
      }
    } else {
      const newEpisode = { ...selectedEpisode, id: uuid(), isNew: true };
      const cpyEpisodes = [...course.episodes];
      cpyEpisodes.push(newEpisode);
      newCourse = {
        ...course,
        episodes: cpyEpisodes,
      };
    }
    setCourse(newCourse);
    handleCloseDrawer();
  };

  const handleSaveBatchDrawer = () => {
    const episodeMap = {};
    course.episodes?.forEach((episode, index) => {
      episodeMap[index + 1] = episode;
    });

    const episodeKeys = [
      ...new Set(
        Object.keys(batchEpisodes).concat(Object.keys(episodeMap) || [])
      ),
    ].sort((a, b) => a - b);
    const newEpisodes = [];
    const trailer = Object.values(batchEpisodes).find(
      (episode) => episode.id === 'trailer'
    );

    episodeKeys.forEach((key) => {
      const batchEpisode = batchEpisodes[key];
      const episode = episodeMap[key];
      if (batchEpisode?.id === 'trailer') {
        return;
      }
      if (batchEpisode && episode && episode.contentType !== contentType.GAME) {
        newEpisodes.push({
          ...episode,
          ...batchEpisode,
          ...(episode.contentType === EPISODE_TYPE.GAME ? { game: null } : {}),
          hasChanged: true,
        });
      } else if (batchEpisode && !isPublished) {
        const newId = uuid();
        newEpisodes.push({
          ...batchEpisode,
          id: newId,
          isNew: true,
        });
      } else if (episode) {
        newEpisodes.push(episode);
      }
    });

    if (newEpisodes.find((ep) => ep.isNew || ep.hasChanged) || trailer) {
      const newCourse = {
        ...course,
        episodes: newEpisodes,
        ...(trailer
          ? {
              trailer: {
                ...(course.trailer || {}),
                ...trailer,
              },
            }
          : {}),
      };
      setCourse(newCourse);
    }
    setBatchEpisodes({});
    handleCloseDrawer();
  };

  const handlePreSaveBatchDrawer = () => {
    const existingEpisodes = Object.keys(batchEpisodes).reduce(
      (acc, episodeKey) => {
        const batchEpisode = batchEpisodes[episodeKey];
        const episodeId = course.episodes[episodeKey - 1]?.id;
        if (episodeId && batchEpisode) {
          const episode = course.episodes.find(
            (episode) => episode.id === episodeId
          );
          const types = [];
          if (
            episode.content?.videoFiles &&
            batchEpisode.content?.file?.processingStatus ===
              PROCESSING_STATUS.TO_SCHEDULE
          ) {
            types.push(LESSON_CONTENT.VIDEO);
          }
          if (
            episode.content?.subtitles.length &&
            batchEpisode?.content?.subtitles?.[0]?.file?.processingStatus ===
              PROCESSING_STATUS.TO_SCHEDULE
          ) {
            types.push(LESSON_CONTENT.SUBTITLE);
          }
          if (types.length) {
            return [
              ...acc,
              {
                item: course.episodes.find(
                  (episode) => episode.id === episodeId
                ),
                types,
              },
            ];
          }
        } else if (batchEpisode?.id === 'trailer') {
          const types = [];
          if (
            course.trailer?.file?.processingStatus &&
            batchEpisode?.file?.processingStatus ===
              PROCESSING_STATUS.TO_SCHEDULE
          ) {
            types.push(LESSON_CONTENT.VIDEO);
          }
          if (types.length) {
            return [
              ...acc,
              {
                item: {
                  id: 'trailer',
                },
                types,
              },
            ];
          }
        }
        return acc;
      },
      []
    );

    if (existingEpisodes.length > 0) {
      setBatchOverwrite(existingEpisodes);
    } else {
      setBatchOverwrite([]);
      handleSaveBatchDrawer();
    }
  };

  const handleConfirmDialog = () => {
    const { newCourse, changedVideoFiles } = confirmationDialogData;
    handleSave(newCourse, changedVideoFiles);
    setConfirmationDialogData();
    setConfirmationDialogOpen(false);
  };
  const handleCancelDialog = () => {
    setConfirmationDialogData();
    setConfirmationDialogOpen(false);
  };
  const handleConfirmPublishDrafts = () => {
    setPublishDraftDialogOpen(false);
    publishDraftEpisodes();
  };
  const handleCancelPublishDrafts = () => {
    setPublishDraftDialogOpen(false);
  };
  const handleOpenBatch = () => {
    handleOpenDrawer(DRAWER_ACTIONS.BATCH);
  };
  const handleCancelOverwriteDialog = () => {
    setBatchOverwrite([]);
  };
  const handleConfirmOverwriteDialog = () => {
    setBatchOverwrite([]);
    handleSaveBatchDrawer();
  };

  const getProcessingStatus = (status) => {
    const processingStatus = {};

    switch (status) {
      case PROCESSING_STATUS.TO_SCHEDULE:
        Object.assign(processingStatus, {
          color: theme.palette.brand,
          label: t('backoffice.courses.lessonsTab.processingStatusToSchedule'),
        });
        break;
      case PROCESSING_STATUS.PROCESSING:
        Object.assign(processingStatus, {
          color: theme.palette.brand,
          label: t('backoffice.courses.lessonsTab.processingStatusProcessing'),
        });
        break;
      case PROCESSING_STATUS.ERROR:
        Object.assign(processingStatus, {
          color: theme.palette.errorDark,
          label: t('backoffice.courses.lessonsTab.processingStatusError'),
        });
        break;
      default:
        return undefined;
    }

    return processingStatus;
  };

  const processingStatusChip = (status, episode) => {
    const isDraft =
      episode?.status === EPISODE_STATUS.DRAFT ||
      (isPublished &&
        episode?.isNew &&
        episode.contentType !== EPISODE_TYPE.GAME);
    const episodeReady = isDraft && isDraftEpisodeReady(episode);

    const draftChip = isDraft ? (
      <Chip
        color={theme.palette.disabledLight}
        label={t('backoffice.courses.lessonsTab.draft')}
        theme="dark"
        small
      />
    ) : null;

    if (episodeReady) {
      return (
        <>
          {draftChip}
          <Chip
            color={theme.palette.successLight}
            label={t('backoffice.courses.lessonsTab.readyToPublish')}
            theme="dark"
            small
          />
        </>
      );
    }

    const processingStatus = getProcessingStatus(status);
    if (isDraft && processingStatus) {
      return (
        <>
          {draftChip}
          <Chip
            color={processingStatus.color}
            label={processingStatus.label}
            theme="dark"
            small
          />
        </>
      );
    }

    if (isDraft) {
      return draftChip;
    }

    if (processingStatus) {
      return (
        <Chip
          color={processingStatus.color}
          label={processingStatus.label}
          theme="dark"
          small
        />
      );
    }
    return null;
  };

  const getTime = ({ contentType: type, content }) => {
    const duration = content?.duration;

    if (!duration) return null;

    const minutes = Math.floor(duration / 60);
    const secondsLeft = `0${duration % 60}`.slice(-2);
    return `${t(
      type === contentType.GAME
        ? 'backoffice.courses.lessonsTab.game'
        : 'backoffice.courses.lessonsTab.video'
    )}  ${minutes}:${secondsLeft}`;
  };

  const getAttachmentStatus = (attachments) => {
    let status = PROCESSING_STATUS_PRIORITY[0];

    attachments.forEach(({ file }) => {
      const attachmentStatus = file?.processingStatus || status;
      if (
        PROCESSING_STATUS_PRIORITY.indexOf(attachmentStatus) >
        PROCESSING_STATUS_PRIORITY.indexOf(status)
      ) {
        status = attachmentStatus;
      }
    });

    return status;
  };

  const getAllStatus = (episode = {}) => {
    const { attachments, content } = episode;
    const { file, subtitles = [] } = content || {};

    let statusAttachments = PROCESSING_STATUS_PRIORITY[0];
    if (attachments && attachments.length > 0) {
      statusAttachments = getAttachmentStatus(attachments);
    }

    const statusVideo = file?.processingStatus || PROCESSING_STATUS_PRIORITY[0];
    const statusSubtitle =
      subtitles[0]?.file?.processingStatus || PROCESSING_STATUS_PRIORITY[0];

    let finalStatus = statusVideo;
    if (
      PROCESSING_STATUS_PRIORITY.indexOf(statusSubtitle) >
      PROCESSING_STATUS_PRIORITY.indexOf(finalStatus)
    ) {
      finalStatus = statusSubtitle;
    }
    if (
      PROCESSING_STATUS_PRIORITY.indexOf(statusAttachments) >
      PROCESSING_STATUS_PRIORITY.indexOf(finalStatus)
    ) {
      finalStatus = statusAttachments;
    }

    return finalStatus;
  };

  const renderItem = (item, index) => {
    if (!episodes[index]) return null;
    const topText = [
      t('backoffice.courses.lessonsTab.lessonTitle', { noLesson: index + 1 }),
    ];

    const timeText = getTime(item);
    if (timeText) {
      topText.push(timeText);
    }

    return (
      <ItemBox
        buttons={
          !isLocked
            ? [
                {
                  handler: () => {
                    handleDeleteEpisode(item.id);
                  },
                  label: t('backoffice.courses.delete'),
                  textStyle: true,
                },
              ]
            : []
        }
        draggable={!isLocked}
        error={presentLanguages.reduce(
          (acc, lang) => acc || !newSelectTranslation(item.name, lang),
          false
        )}
        id={item.id}
        key={item.id}
        menuItems={[]}
        onClickHandler={(id) => {
          const clickedEpisode = episodes.find((episode) => episode.id === id);
          setSelectedEpisode(clickedEpisode);
          setOriginalSelectedEpisode(clickedEpisode);
          handleOpenDrawer(DRAWER_ACTIONS.EDIT_LESSON);
        }}
        selected={item.id === originalSelectedEpisode?.id}
        theme="dark"
        title={newSelectTranslation(item.name, locale)}
        topText={topText}
        topTextUppercase={false}
        topTag={processingStatusChip(
          getAllStatus(episodes[index]),
          episodes[index]
        )}
      />
    );
  };

  const trailerButton = [];
  if (!isLocked && !hasTrailer) {
    trailerButton.push({
      handler: () => {
        const newEpisode = {
          id: 'trailer',
          content: {
            duration: 0,
            file: { processingStatus: null },
            subtitles: [],
            videoFiles: [],
          },
        };
        setSelectedEpisode(newEpisode);
        setOriginalSelectedEpisode(newEpisode);
        handleOpenDrawer(DRAWER_ACTIONS.ADD_TRAILER);
      },
      label: t('backoffice.courses.add'),
      textStyle: true,
    });
  }
  if (!isPublished && hasTrailer) {
    trailerButton.push({
      handler: handleDeleteTrailer,
      label: t('backoffice.courses.delete'),
      textStyle: true,
    });
  }

  let drawerTitle;
  const { hasChanged: _, ...cleanSelectedEpisode } = selectedEpisode || {};
  let drawerContent = (
    <DrawerLesson
      course={course}
      errors={errorsDrawerLesson}
      item={cleanSelectedEpisode}
      locked={isLocked}
      onChange={handleChangeDrawer}
      onSubtitleError={setSubtitleError}
      setChangedDrawerContent={setChangedDrawerContent}
      submitting={submitting}
      type={drawer || null}
    />
  );
  let drawerSave;
  switch (drawer) {
    case DRAWER_ACTIONS.ADD_LESSON:
      drawerTitle = t('backoffice.courses.lessonsTab.lessonsNew');
      drawerSave = handleSaveLessonDrawer;
      break;
    case DRAWER_ACTIONS.EDIT_TRAILER:
    case DRAWER_ACTIONS.ADD_TRAILER:
      drawerTitle = `${t(
        'backoffice.courses.lessonsTab.trailer'
      )} - ${newSelectTranslation(course.name, locale)}`;
      drawerSave = handleSaveLessonDrawer;
      break;
    case DRAWER_ACTIONS.BATCH:
      drawerTitle = isPublished
        ? t('backoffice.courses.lessonsTab.batchSubtitlesUpload')
        : t('backoffice.courses.lessonsTab.batchUpload');
      drawerSave = handlePreSaveBatchDrawer;
      drawerContent = (
        <DrawerBatch
          onChange={(items) => handleChangeBatchDrawer(items)}
          setChangedDrawerContent={setChangedDrawerContent}
          setError={setErrorDrawer}
        />
      );
      break;
    case DRAWER_ACTIONS.EDIT_LESSON:
      drawerTitle = selectedEpisode
        ? newSelectTranslation(selectedEpisode.name, locale)
        : '';
      drawerSave = handleSaveLessonDrawer;
      break;
    default:
      drawerTitle = '';
      break;
  }

  const isSaveDrawerDisabled =
    locked ||
    errorDrawer !== null ||
    subtitleError !== null ||
    errorsDrawerLesson.length > 0 ||
    uploadingFiles ||
    (isPublished &&
      drawer === DRAWER_ACTIONS.EDIT_LESSON &&
      selectedEpisode?.contentType !== contentType.GAME &&
      selectedEpisode?.content?.videoFiles === null) ||
    (isPublished &&
      drawer === DRAWER_ACTIONS.EDIT_TRAILER &&
      course.trailer?.videoFiles === null);

  const topTextTrailer = [];
  let timeText;
  if (course.trailer) {
    timeText = getTime({ content: course.trailer });
  }
  if (timeText) {
    topTextTrailer.push(timeText);
  }

  return (
    <SectionStyled>
      <Dialog
        actions={[
          {
            ariaLabel: t(
              'backoffice.courses.lessonsTab.confirmationDialogCancel'
            ),
            handler: handleCancelDialog,
            label: t('backoffice.courses.lessonsTab.confirmationDialogCancel'),
            type: 'secondaryOutline',
          },
          {
            ariaLabel: t(
              'backoffice.courses.lessonsTab.confirmationDialogConfirm'
            ),
            disabled: uploadingFiles,
            handler: handleConfirmDialog,
            label: t('backoffice.courses.lessonsTab.confirmationDialogConfirm'),
          },
        ]}
        ariaLabel={t(
          'backoffice.courses.lessonsTab.confirmationDialogArialabel'
        )}
        confirmationTitle={t(
          'backoffice.courses.lessonsTab.confirmationDialogTitle'
        )}
        onClose={handleCancelDialog}
        open={confirmationDialogOpen}
      >
        <T color={theme.palette.pureDark}>
          {t('backoffice.courses.lessonsTab.confirmationDialogMessage', {
            channel: t(
              `backoffice.courses.lessonsTab.processingSlack_${domain}`
            ),
          })}
        </T>
        <T color={theme.palette.pureDark} sx={{ marginTop: theme.spacing.m1 }}>
          {t('backoffice.courses.lessonsTab.confirmationDialogWarning')}
        </T>
      </Dialog>
      <Dialog
        actions={[
          {
            ariaLabel: t('backoffice.courses.lessonsTab.batchDialogCancel'),
            handler: handleCancelOverwriteDialog,
            label: t('backoffice.courses.lessonsTab.batchDialogCancel'),
            type: 'secondaryOutline',
          },
          {
            ariaLabel: t('backoffice.courses.lessonsTab.batchDialogConfirm'),
            disabled: uploadingFiles,
            handler: handleConfirmOverwriteDialog,
            label: t('backoffice.courses.lessonsTab.batchDialogConfirm'),
          },
        ]}
        ariaLabel={t(
          `backoffice.courses.lessonsTab.${
            isPublished
              ? 'batchSubtitleDialogArialabel'
              : 'batchDialogArialabel'
          }`
        )}
        onClose={handleCancelOverwriteDialog}
        open={batchOverwrite.length > 0}
      >
        <T color={theme.palette.pureDark}>
          {t(
            `backoffice.courses.lessonsTab.${
              isPublished ? 'batchSubtitlesDialogMessage' : 'batchDialogMessage'
            }`,
            {
              count: batchOverwrite.length,
            }
          )}
        </T>
        <ReplaceLessonsList>
          {batchOverwrite.map(({ item, types }) => (
            <T
              color={theme.palette.pureDark}
              key={item.id}
              fontWeight="semibold"
            >
              {item.id === 'trailer'
                ? `00 - ${t('backoffice.courses.lessonsTab.trailer')}`
                : `${`0${item.ordinal}`.slice(-2)} - ${newSelectTranslation(
                    item.name,
                    locale,
                    ''
                  )}`}
              {!isPublished
                ? ` (${types
                    .map((type) =>
                      t(`backoffice.courses.lessonsTab.batchDialog.${type}`)
                    )
                    .join(', ')})`
                : ''}
            </T>
          ))}
        </ReplaceLessonsList>
      </Dialog>
      <Dialog
        actions={[
          {
            ariaLabel: t('backoffice.courses.lessonsTab.publishDraftCancel'),
            handler: handleCancelPublishDrafts,
            label: t('backoffice.courses.lessonsTab.publishDraftCancel'),
            type: 'secondaryOutline',
          },
          {
            ariaLabel: t('backoffice.courses.lessonsTab.publishDraftConfirm'),
            handler: handleConfirmPublishDrafts,
            label: t('backoffice.courses.lessonsTab.publishDraftConfirm'),
          },
        ]}
        ariaLabel={t('backoffice.courses.lessonsTab.publishDraftDialogTitle')}
        confirmationTitle={t(
          'backoffice.courses.lessonsTab.publishDraftDialogTitle'
        )}
        onClose={handleCancelPublishDrafts}
        open={publishDraftDialogOpen}
      >
        <T color={theme.palette.pureDark}>
          {t('backoffice.courses.lessonsTab.publishDraftDialogMessage', {
            count: draftEpisodes.length,
          })}
        </T>
      </Dialog>
      <HeaderStyled>
        <ButtonGroupContainer>
          <Button
            disabled={isLocked}
            iconRight={AddIcon}
            onClick={() => {
              const newEpisode = {
                attachments: [],
                content: {
                  duration: 0,
                  file: { processingStatus: null },
                  subtitles: [],
                  videoFiles: [],
                },
                ordinal: course.episodes.length + 1,
                name: [],
              };
              setSelectedEpisode(newEpisode);
              setOriginalSelectedEpisode(newEpisode);
              handleOpenDrawer(DRAWER_ACTIONS.ADD_LESSON);
            }}
            sx={{ textTransform: 'uppercase' }}
            type={
              isOpenDrawer && drawer === DRAWER_ACTIONS.ADD_LESSON
                ? 'secondary'
                : 'secondaryOutline'
            }
          >
            {t('backoffice.courses.lessonsTab.lessonAdd')}
          </Button>
          {!isLocked && (
            <Button
              iconRight={AddIcon}
              onClick={handleOpenBatch}
              sx={{ textTransform: 'uppercase' }}
              type={
                isOpenDrawer && drawer === DRAWER_ACTIONS.BATCH
                  ? 'secondary'
                  : 'secondaryOutline'
              }
            >
              {isPublished
                ? t('backoffice.courses.lessonsTab.batchSubtitlesUpload')
                : t('backoffice.courses.lessonsTab.batch')}
            </Button>
          )}
        </ButtonGroupContainer>
        <ButtonGroupContainer>
          <Button
            disabled={canSave || submitting}
            iconLeft={ReplayIcon}
            loading={submittingRefresh}
            onClick={fetchLessonsProcessingStatus}
            type="secondaryOutline"
          >
            {t('backoffice.courses.refresh')}
          </Button>
          {hasDraftEpisodes && (
            <Tooltip
              arrow
              title={
                canPublishDrafts ? (
                  ''
                ) : (
                  <>
                    <T color={theme.palette.mainDark}>
                      {t('backoffice.courses.lessonsTab.publishDraftTooltip')}
                    </T>
                    <ul>
                      {!locked && (
                        <T color={theme.palette.mainDark} component="li">
                          {t('backoffice.courses.lessonsTab.publishDraftLock')}
                        </T>
                      )}
                      {!allDraftEpisodesProcessed && (
                        <T color={theme.palette.mainDark} component="li">
                          {t(
                            'backoffice.courses.lessonsTab.publishDraftProcessing'
                          )}
                        </T>
                      )}
                    </ul>
                  </>
                )
              }
            >
              <div>
                <Button
                  disabled={!canPublishDrafts}
                  loading={submitting}
                  onClick={() => setPublishDraftDialogOpen(true)}
                >
                  {t('backoffice.courses.lessonsTab.publishDraftEpisodes')}
                </Button>
              </div>
            </Tooltip>
          )}
          <Tooltip
            arrow
            title={
              course.status === courseStatus.PUBLISHED &&
              !canSave &&
              !submitting &&
              !locked &&
              Object.keys(currentTabValidation)?.length ? (
                <>
                  <T color={theme.palette.mainDark}>
                    {t('backoffice.courses.publishing.save')}
                  </T>
                  <ValidationList details={currentTabValidation} />
                </>
              ) : (
                ''
              )
            }
          >
            <div>
              <Button
                disabled={!canSave}
                loading={submitting}
                onClick={handlePreSave}
                type="secondary"
              >
                {t('generic.save')}
              </Button>
            </div>
          </Tooltip>
        </ButtonGroupContainer>
      </HeaderStyled>
      <Box>
        <ItemBox
          buttons={trailerButton}
          id={course.id}
          key={course.id}
          menuItems={[]}
          onClickHandler={
            hasTrailer
              ? () => {
                  const newEpisode = {
                    id: 'trailer',
                    content: course.trailer,
                  };
                  setSelectedEpisode(newEpisode);
                  setOriginalSelectedEpisode(newEpisode);
                  handleOpenDrawer(DRAWER_ACTIONS.EDIT_TRAILER);
                }
              : null
          }
          selected={originalSelectedEpisode?.id === 'trailer'}
          theme="dark"
          title={
            hasTrailer
              ? t('backoffice.courses.lessonsTab.trailer')
              : t('backoffice.courses.lessonsTab.trailerTitleEmpty')
          }
          topTextUppercase={false}
          topText={hasTrailer && topTextTrailer}
          topTag={processingStatusChip(course.trailer?.file?.processingStatus)}
        />
      </Box>
      <Box height="600px" header={<LessonCustomHeader episodes={episodes} />}>
        {(episodes.length > 0 && (
          <DragAndDrop
            isLocked={isLocked}
            items={episodes}
            onDragEnd={handleDragEndEpisode}
            renderItem={renderItem}
          />
        )) ||
          null}
      </Box>
      <Drawer
        anchor="right"
        open={isOpenDrawer}
        onClose={handleCloseDrawer}
        onSave={drawerSave}
        errorMessage={errorDrawer}
        saveDisabled={isSaveDrawerDisabled}
        sx={{
          '.MuiDrawer-paperAnchorRight': {
            width: '522px',
          },
        }}
        title={drawerTitle}
        edited={changedDrawerContent && errorDrawer === null}
      >
        {drawerContent}
      </Drawer>
    </SectionStyled>
  );
};

export default Lessons;
